/**
 * Human-sized application routes.
 *
 * Plot configuration remains a query-string wire format internally, but the
 * address bar only names the place a person is looking at. These helpers are
 * pure so routing and legacy-query restoration cannot quietly drift apart.
 */

import type { AssistantTab, ModelTab, Section, Tab } from './store.ts';

export interface AppRoute {
  section: Section;
  tab?: Tab;
  modelTab?: ModelTab;
  assistantTab?: AssistantTab;
  dashboardId?: string;
  /** The place inside a registered section: `/argus/rules` -> `rules`,
   *  `/argus/inbox/42` -> `inbox/42`. At most two levels. */
  sub?: string;
}

/**
 * A section a feature adds without this module knowing its name - the same
 * pattern as the lens registry. It gets the path `/<section>[/<sub>]`, and a
 * refresh or a shared link brings a person back to it. Whether they may see it
 * is still the shell's call (`enforceVisibility`), not the route's.
 */
export interface SectionRoute {
  section: Section;
  /** The place inside the section to put in the path, if any. */
  sub?: () => string | null;
  /** Go back to that place after a load. Unknown values should be ignored. */
  restoreSub?: (sub: string) => void;
}

const sectionRoutes = new Map<string, SectionRoute>();

export function registerSectionRoute(route: SectionRoute): void {
  sectionRoutes.set(route.section, route);
}

export function sectionRoute(section: string | null | undefined): SectionRoute | undefined {
  return section ? sectionRoutes.get(section) : undefined;
}

export interface AppRouteState {
  section: Section;
  tab: Tab;
  modelTab: ModelTab;
  assistantTab: AssistantTab;
  dashboardId: string | null;
  dashboardOpen: boolean;
}

function pathParts(pathname: string): string[] {
  return pathname
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .filter(Boolean);
}

/** Recognise only routes owned by the SPA; `/`, `/s/…`, `/c/…`, `/admin`, and
 *  unknown paths are deliberately left to their own boot paths. */
export function parseAppRoute(pathname: string): AppRoute | null {
  const parts = pathParts(pathname);
  if (!parts.length) return null;

  const registered = sectionRoutes.get(parts[0] as string);
  // Up to two places deep: `/argus/inbox/42` - a room, and one thing in it.
  if (registered && parts.length <= 3) {
    let sub: string | undefined;
    if (parts.length > 1) {
      try {
        sub = parts.slice(1).map(decodeURIComponent).join('/');
      } catch {
        return null;
      }
    }
    return { section: registered.section, ...(sub ? { sub } : {}) };
  }

  if (parts.length === 1 && parts[0] === 'home') return { section: 'home' };
  if (parts[0] === 'explore') {
    if (parts.length === 1 || (parts.length === 2 && parts[1] === 'plot')) {
      return { section: 'explore', tab: 'plot' };
    }
    if (parts.length === 2 && parts[1] === 'export') {
      return { section: 'explore', tab: 'export' };
    }
    if (
      parts[1] === 'model-viewer' &&
      parts.length <= 3 &&
      (!parts[2] || parts[2] === 'service-power' || parts[2] === 'sfoc')
    ) {
      return {
        section: 'explore',
        tab: 'modelviewer',
        modelTab: parts[2] === 'sfoc' ? 'sfoc' : 'sp',
      };
    }
    return null;
  }
  if (parts[0] === 'dashboards' && parts.length <= 2) {
    let dashboardId: string | undefined;
    if (parts[1]) {
      try {
        dashboardId = decodeURIComponent(parts[1]);
      } catch {
        return null;
      }
    }
    return { section: 'dashboards', ...(dashboardId ? { dashboardId } : {}) };
  }
  if (parts.length === 1 && parts[0] === 'catalog') return { section: 'catalog' };
  if (
    parts[0] === 'assistant' &&
    parts.length <= 2 &&
    (!parts[1] || parts[1] === 'chat' || parts[1] === 'history')
  ) {
    return {
      section: 'assistant',
      assistantTab: parts[1] === 'history' ? 'history' : 'chat',
    };
  }
  return null;
}

/** Overlay an explicit clean route onto a stored workspace query. The route
 *  wins for navigation; every chart-setting parameter remains untouched. */
export function queryWithAppRoute(query: string, pathname: string): string {
  const route = parseAppRoute(pathname);
  if (!route) return query.replace(/^\?/, '');

  const params = new URLSearchParams(query.replace(/^\?/, ''));
  for (const key of ['section', 'tab', 'mt', 'atab', 'db']) params.delete(key);
  params.set('section', route.section);
  if (route.tab) params.set('tab', route.tab);
  if (route.modelTab) params.set('mt', route.modelTab);
  if (route.assistantTab) params.set('atab', route.assistantTab);
  if (route.dashboardId) params.set('db', route.dashboardId);
  return params.toString();
}

/** The clean path for the current application destination. */
export function buildAppPath(state: AppRouteState): string {
  const registered = sectionRoutes.get(state.section);
  if (registered) {
    const sub = registered.sub?.();
    return sub
      ? `/${state.section}/${sub.split('/').map(encodeURIComponent).join('/')}`
      : `/${state.section}`;
  }
  if (state.section === 'home') return '/home';
  if (state.section === 'dashboards') {
    return state.dashboardOpen && state.dashboardId
      ? `/dashboards/${encodeURIComponent(state.dashboardId)}`
      : '/dashboards';
  }
  if (state.section === 'catalog') return '/catalog';
  if (state.section === 'assistant') {
    return state.assistantTab === 'history' ? '/assistant/history' : '/assistant';
  }
  if (state.tab === 'export') return '/explore/export';
  if (state.tab === 'modelviewer') {
    return state.modelTab === 'sfoc'
      ? '/explore/model-viewer/sfoc'
      : '/explore/model-viewer/service-power';
  }
  return '/explore/plot';
}

export function dashboardIdFromPath(pathname: string): string | null {
  const route = parseAppRoute(pathname);
  return route?.section === 'dashboards' ? route.dashboardId ?? null : null;
}
